import React, { useMemo, useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import {
	Add as AddIcon,
	AttachFile as AttachIcon,
	FileUploadOutlined as UploadIcon,
	AutoAwesomeOutlined as PromptsIcon,
} from "@mui/icons-material";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { buildRows } from "../../utils/promptLibrary";
import PromptRowContent from "./PromptRowContent";

const TRIGGER_SX = { color: "var(--w-text-muted)" };
const ITEM_SX = { fontSize: "14px", color: "var(--w-text)", gap: 1, minHeight: 40, whiteSpace: "normal", overflowWrap: "anywhere" };

/**
 * chat-experience 06c — the composer's `+` (§5.2, D-30): **Upload file** · **Prompts ›**.
 *
 * One MUI <Menu> with two views ("root" | "prompts") instead of a nested popover — a nested submenu
 * has no touch story; a view swap keeps focus inside one popover. The Prompts view is the same row list
 * as the starters (D-40): top-level prompts first, then each collection expanding in place (D-36).
 * Choosing a prompt closes the menu and raises `onPromptSelect(entry)`, which sends it (D-34).
 *
 * With no library (§6 gap 7) the trigger IS today's paperclip: one click opens the picker, no menu.
 * `uploadDisabled` (a full attachment tray) greys only the Upload row; `disabled` (a data-collector
 * flow) disables the trigger.
 */
export default function PlusMenu({ prompts, onUploadClick, onPromptSelect, uploadDisabled = false, disabled = false }) {
	const { msg } = useWidgetMsg();
	const [anchorEl, setAnchorEl] = useState(null);
	const [view, setView] = useState("root");
	const [backFromPrompts, setBackFromPrompts] = useState(false);
	const [openCollection, setOpenCollection] = useState(null);
	const rows = useMemo(() => buildRows(prompts, openCollection), [prompts, openCollection]);

	if (rows.length === 0) {
		return (
			<IconButton
				size="small"
				sx={TRIGGER_SX}
				onClick={onUploadClick}
				disabled={disabled || uploadDisabled}
				aria-label={msg("starters.upload", "Upload file")}
			>
				<AttachIcon fontSize="small" />
			</IconButton>
		);
	}

	const open = !!anchorEl;
	const close = () => {
		setAnchorEl(null);
		setView("root");
		setBackFromPrompts(false);
		setOpenCollection(null);
	};
	const showPrompts = () => { setBackFromPrompts(false); setView("prompts"); };
	const showRoot = () => { setBackFromPrompts(true); setView("root"); };
	const promptsLabel = msg("starters.prompts", "Prompts");

	const items = view === "root" ? [
		<MenuItem key="upload" sx={ITEM_SX} disabled={uploadDisabled} onClick={() => { close(); onUploadClick(); }}>
			<UploadIcon fontSize="small" sx={{ color: "var(--w-accent)" }} />
			{msg("starters.upload", "Upload file")}
		</MenuItem>,
		<MenuItem key="prompts" sx={ITEM_SX} autoFocus={backFromPrompts} onClick={showPrompts}>
			<PromptsIcon fontSize="small" sx={{ color: "var(--w-accent)" }} />
			{promptsLabel}
			<span aria-hidden="true" style={{ marginInlineStart: "auto" }}>{"›"}</span>
		</MenuItem>,
	] : [
		<MenuItem key="back" sx={ITEM_SX} autoFocus onClick={showRoot} aria-label={msg("starters.back", "Back")}>
			<span aria-hidden="true">{"‹"}</span>
			{promptsLabel}
		</MenuItem>,
		...rows.map((row) => (row.type === "collection" ? (
			<MenuItem
				key={row.key}
				sx={ITEM_SX}
				aria-expanded={row.open}
				aria-label={msg("starters.collection", "{name}, {count} prompts", { name: row.name, count: row.count })}
				onClick={() => setOpenCollection(row.open ? null : row.name)}
			>
				<PromptRowContent row={row} />
			</MenuItem>
		) : (
			<MenuItem
				key={row.key}
				sx={{ ...ITEM_SX, ...(row.depth > 0 ? { paddingInlineStart: "32px" } : {}) }}
				aria-label={msg("starters.send", "Send: {label}", { label: row.prompt.label })}
				onClick={() => { close(); onPromptSelect(row.prompt); }}
			>
				<PromptRowContent row={row} />
			</MenuItem>
		))),
	];

	return (
		<>
			<IconButton
				size="small"
				sx={TRIGGER_SX}
				disabled={disabled}
				aria-label={msg("starters.menu", "Add to this message")}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				<AddIcon fontSize="small" />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={close}
				anchorOrigin={{ vertical: "top", horizontal: "left" }}
				transformOrigin={{ vertical: "bottom", horizontal: "left" }}
				slotProps={{ paper: { sx: { borderRadius: "12px", border: "1px solid var(--w-border)", minWidth: 228, maxWidth: 320, maxHeight: 320 } } }}
			>
				{items}
			</Menu>
		</>
	);
}
